import React, { useEffect, useState } from "react";
import {
    collection,
    getDocs,
    query,
    where,
    addDoc,
    doc,
    getDoc,
    orderBy,
    updateDoc,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import QRCode from "qrcode";
import Swal from "sweetalert2";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    CircularProgress,
    Alert,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";

function Seatingqrcodes() {
    const [propertyId, setPropertyId] = useState(null);
    const [seatings, setSeatings] = useState([]);
    const [loadingTable, setLoadingTable] = useState(null);
    const [generatedQR, setGeneratedQR] = useState({});
    const [customerQr, setCustomerQr] = useState("No");
    const [loading, setLoading] = useState(true);
    const [propertyLocation, setPropertyLocation] = useState(null);
    // ✅ Fetch property + customerQr
    useEffect(() => {
        const fetchData = async () => {
            try {
                const user = auth.currentUser;
                if (!user) return;

                const roleSnap = await getDoc(doc(db, "authroles", user.uid));
                if (!roleSnap.exists()) return;

                const propId = roleSnap.data().propertyId;
                setPropertyId(propId);

                // Fetch property details
                const propertyQuery = query(
                    collection(db, "properties"),
                    where("propertyId", "==", propId)
                );

                const propertySnap = await getDocs(propertyQuery);

                if (!propertySnap.empty) {
                    if (!propertySnap.empty) {
                        const propertyData = propertySnap.docs[0].data();
                        setCustomerQr(propertyData.customerQr || "No");

                        // ✅ Save location to state
                        if (propertyData.latitude && propertyData.longitude) {
                            setPropertyLocation({
                                latitude: propertyData.latitude,
                                longitude: propertyData.longitude,
                            });
                        }
                    }
                }

                setLoading(false);
            } catch (error) {
                console.log(error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // ✅ Fetch seatings
    useEffect(() => {
        if (!propertyId) return;

        const fetchSeatings = async () => {
            const q = query(
                collection(db, "seatings"),
                where("propertyId", "==", propertyId),
                orderBy("createdAt", "asc")
            );

            const snapshot = await getDocs(q);

            const tables = snapshot.docs.map((doc) => doc.data());

            const uniqueTables = [
                ...new Map(tables.map((item) => [item.seatname, item])).values(),
            ];

            setSeatings(uniqueTables);
        };

        fetchSeatings();
    }, [propertyId]);

    const generateQR = async (tableid) => {
        try {
            if (customerQr !== "Yes") {
                Swal.fire("Disabled", "Customer QR is disabled for this property", "warning");
                return;
            }

            setLoadingTable(tableid);

            // 🔹 Step 1: Check Property Location
            const propertyQuery = query(
                collection(db, "properties"),
                where("propertyId", "==", propertyId)
            );

            const propertySnap = await getDocs(propertyQuery);

            if (propertySnap.empty) {
                Swal.fire("Error", "Property not found", "error");
                return;
            }

            const propertyDoc = propertySnap.docs[0];
            const propertyData = propertyDoc.data();

            let latitude = propertyData.latitude;
            let longitude = propertyData.longitude;

            // 🔥 Step 2: If location NOT exists → Capture it
            if (!latitude || !longitude) {
                if (!navigator.geolocation) {
                    Swal.fire("Error", "Location not supported in this browser", "error");
                    return;
                }

                const position = await new Promise((resolve, reject) =>
                    navigator.geolocation.getCurrentPosition(resolve, reject)
                );

                latitude = position.coords.latitude;
                longitude = position.coords.longitude;

                // 🔥 Step 3: Update property document with location
                await updateDoc(propertyDoc.ref, {
                    latitude,
                    longitude,
                    geoRadius: 100,
                    updatedAt: new Date(),
                });
                Swal.fire("Location Saved", "Restaurant location captured successfully", "success");
            }

            // 🔹 Step 4: Generate QR
            const link = `http://localhost:3000/menu/${propertyId}/${tableid}`;
            const qrImage = await QRCode.toDataURL(link);

            await addDoc(collection(db, "seatingqrcodes"), {
                link,
                propertyid: propertyId,
                tableid,
                qrcode: qrImage,
                status: "Active",
                createdAt: new Date(),
            });

            setGeneratedQR((prev) => ({
                ...prev,
                [tableid]: qrImage,
            }));

            Swal.fire("Success", "QR Generated Successfully", "success");
        } catch (error) {
            console.log(error);
            Swal.fire("Error", "Something went wrong", "error");
        } finally {
            setLoadingTable(null);
        }
    };
    if (loading) {
        return (
            <Box p={3}>
                <CircularProgress />
            </Box>
        );
    }

    const downloadQR = (tableid) => {
        const qrImage = generatedQR[tableid];
        if (!qrImage) return;

        const link = document.createElement("a");
        link.href = qrImage;
        link.download = `${tableid}-QR.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box className=" p-3">
            <Typography variant="h4" fontWeight="bold" color="white" mb={3}>
                Seating QR Management
            </Typography>

            {/* 🔴 If QR Disabled */}
            {customerQr !== "Yes" && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Customer QR is currently disabled for this property.
                </Alert>
            )}

            <Grid className="row" spacing={3}>
                {seatings.map((table, index) => (
                    <Grid className="col-sm-2 mb-3" xs={12} sm={6} md={3} key={index}>
                        <Card
                            sx={{
                                borderRadius: 4,
                                boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                transition: "0.3s",
                                "&:hover": {
                                    transform: "translateY(-5px)",
                                },
                            }}
                        >
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold">
                                    {table.seatname}
                                </Typography>

                                {generatedQR[table.seatname] && (
                                    <>
                                        <Box
                                            component="img"
                                            src={generatedQR[table.seatname]}
                                            alt="QR Code"
                                            sx={{
                                                width: "100%",
                                                maxWidth: 180,
                                                display: "block",
                                                margin: "10px auto",
                                            }}
                                        />

                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            sx={{
                                                mt: 1,
                                                borderRadius: 3,
                                                textTransform: "none",
                                                fontWeight: "bold",
                                            }}
                                            onClick={() => downloadQR(table.seatname)}
                                        >
                                            Download QR
                                        </Button>
                                    </>
                                )}

                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<QrCode2Icon />}
                                    onClick={() => generateQR(table.seatname)}
                                    disabled={
                                        loadingTable === table.seatname ||
                                        customerQr !== "Yes"
                                    }
                                    sx={{
                                        mt: 2,
                                        borderRadius: 3,
                                        textTransform: "none",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {loadingTable === table.seatname ? (
                                        <CircularProgress size={22} color="inherit" />
                                    ) : (
                                        "Generate QR"
                                    )}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {seatings.length === 0 && (
                <Typography mt={3} color="text.secondary">
                    No Tables Found
                </Typography>
            )}

            {propertyLocation && (
                <Card sx={{ mb: 3, borderRadius: 3 }}>
                    <CardContent>
                        <Typography variant="h6" fontWeight="bold" mb={2}>
                            Restaurant Location
                        </Typography>

                        <iframe
                            width="100%"
                            height="250"
                            style={{ borderRadius: "12px" }}
                            loading="lazy"
                            allowFullScreen
                            src={`https://www.google.com/maps?q=${propertyLocation.latitude},${propertyLocation.longitude}&z=17&output=embed`}
                        ></iframe>

                        <Typography variant="body2" mt={1}>
                            Lat: {propertyLocation.latitude} | Lng: {propertyLocation.longitude}
                        </Typography>
                    </CardContent>
                </Card>
            )}


        </Box>
    );
}

export default Seatingqrcodes;